import React, { useState } from 'react';
import { Slider } from '../ui/slider';
import { TrendingDown, AlertTriangle, Calculator, Sparkles } from 'lucide-react';
import { calculatorDefaults } from '../../data/mock';

const CalculatorSection = () => {
  const [citasPerdidas, setCitasPerdidas] = useState(calculatorDefaults.defaultCitas);
  const [valorTratamiento, setValorTratamiento] = useState(calculatorDefaults.defaultValor);

  const dineroQuemado = citasPerdidas * valorTratamiento;
  const dineroAnual = dineroQuemado * 12;
  const recuperable = Math.round(dineroQuemado * 0.8);

  return (
    <section className="md:py-28 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden !pt-[50px] !pb-[80px] !pl-[16px] !pr-[16px]">
      {/* Background Decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-red-50/50 to-transparent rounded-full blur-3xl"></div>
      
      <div className="relative max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-red-100 to-rose-100 text-red-500 mb-6 shadow-lg shadow-red-100">
            <Calculator className="w-8 h-8" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            ¿Cuánto dinero estás perdiendo cada mes?
          </h2>
          <p className="text-gray-600 text-lg">
            Mueve los controles y descubre el impacto real de los no-shows
          </p>
        </div>

        {/* Calculator Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100 relative overflow-hidden">
          {/* Decorative corner */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-red-50 to-transparent"></div>
          
          {/* Slider 1: Citas perdidas */}
          <div className="mb-10 relative">
            <div className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-semibold text-lg">
                Citas perdidas al mes
              </label>
              <span className="text-3xl font-bold text-[#007AFF] bg-blue-50 px-4 py-1 rounded-lg">
                {citasPerdidas}
              </span>
            </div>
            <Slider
              value={[citasPerdidas]}
              onValueChange={(value) => setCitasPerdidas(value[0])}
              min={calculatorDefaults.minCitas}
              max={calculatorDefaults.maxCitas}
              step={1}
              className="w-full" />

            <div className="flex justify-between text-sm text-gray-400 mt-3">
              <span>{calculatorDefaults.minCitas} cita</span>
              <span>{calculatorDefaults.maxCitas} citas</span>
            </div>
          </div>

          {/* Slider 2: Valor tratamiento */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-semibold text-lg">
                Valor medio del tratamiento
              </label>
              <span className="text-3xl font-bold text-[#007AFF] bg-blue-50 px-4 py-1 rounded-lg">
                {valorTratamiento}€
              </span>
            </div>
            <Slider
              value={[valorTratamiento]}
              onValueChange={(value) => setValorTratamiento(value[0])}
              min={calculatorDefaults.minValor}
              max={calculatorDefaults.maxValor}
              step={10}
              className="w-full" />

            <div className="flex justify-between text-sm text-gray-400 mt-3">
              <span>{calculatorDefaults.minValor}€</span>
              <span>{calculatorDefaults.maxValor}€</span>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t-2 border-dashed border-gray-200 my-8"></div>

          {/* Results */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Monthly Loss */}
            <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-2xl p-6 text-center relative overflow-hidden border border-red-100 group hover:shadow-lg transition-shadow">
              <div className="absolute top-3 right-3 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-red-600 text-sm font-semibold mb-2 uppercase tracking-wide">Pérdida mensual</p>
              <p className="text-5xl md:text-6xl font-extrabold text-red-600 mb-2">
                {dineroQuemado.toLocaleString('es-ES')}€
              </p>
              <p className="text-red-500/70 text-sm">que podrías recuperar</p>
            </div>

            {/* Annual Loss */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 text-center relative overflow-hidden group hover:shadow-lg transition-shadow">
              <div className="absolute top-3 right-3 w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <p className="text-gray-400 text-sm font-semibold mb-2 uppercase tracking-wide">Pérdida anual</p>
              <p className="text-5xl md:text-6xl font-extrabold text-white mb-2">
                {dineroAnual.toLocaleString('es-ES')}€
              </p>
              <p className="text-gray-500 text-sm">escapándose de tu clínica</p>
            </div>
          </div>

          {/* Recovery Message */}
          <div className="mt-8 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 relative overflow-hidden">
            <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-10">
              <Sparkles className="w-20 h-20 text-blue-500" />
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#007AFF] flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/30">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-[#007AFF] font-bold text-lg">
                  Con No-Show Killer podrías recuperar hasta <span className="text-2xl">{recuperable.toLocaleString('es-ES')}€/mes</span>
                </p>
                <p className="!text-sm text-blue-600/70">Basado en el 80% de reducción promedio de "sillas vacias"</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default CalculatorSection;